import { useEffect, useState } from "react";
import { Drawer, Table, Tag, Typography, Select, Space } from "antd";
import { getHistorialProyecto } from "../../../services/informeTecnicoFinal/informeTecnicoFinalService";

const { Text } = Typography;

const NAVY = "#042956";

const ACCION_LABELS = {
  acceso_modulo: { label: "Acceso al módulo", color: "default" },
  ver_carpeta: { label: "Navegación", color: "default" },
  descarga: { label: "Descarga", color: "blue" },
  subida: { label: "Subida", color: "green" },
  actualizacion: { label: "Actualización", color: "gold" },
  renombrado: { label: "Renombrado", color: "purple" },
  creacion_carpeta: { label: "Carpeta creada", color: "cyan" },
  eliminacion: { label: "Eliminación", color: "red" }
};

function formatFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "—";
  }
}

/**
 * HistorialPanel
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - codigo: código del proyecto (Informe Técnico Final)
 */
export default function HistorialPanel({ open, onClose, codigo }) {
  const [loading, setLoading] = useState(false);
  const [registros, setRegistros] = useState([]);
  const [filtroUsuario, setFiltroUsuario] = useState("todos");
  const [filtroAccion, setFiltroAccion] = useState("todos");

  useEffect(() => {
    if (!open || !codigo) return;
    setFiltroUsuario("todos");
    setFiltroAccion("todos");

    const cargar = async () => {
      setLoading(true);
      const { data } = await getHistorialProyecto(codigo);
      setRegistros(data || []);
      setLoading(false);
    };

    cargar();
  }, [open, codigo]);

  const usuarios = [...new Set(registros.map((r) => r.username).filter(Boolean))];
  const registrosFiltrados = registros.filter((r) => {
    const coincideUsuario = filtroUsuario === "todos" || r.username === filtroUsuario;
    const coincideAccion = filtroAccion === "todos" || r.accion === filtroAccion;
    return coincideUsuario && coincideAccion;
  });

  const columns = [
    {
      title: "Usuario",
      dataIndex: "username",
      key: "username",
      width: 140,
      render: (v) => <Text style={{ color: NAVY, fontWeight: 500 }}>{v || "—"}</Text>
    },
    {
      title: "Acción",
      dataIndex: "accion",
      key: "accion",
      width: 150,
      render: (accion) => {
        const info = ACCION_LABELS[accion] || { label: accion, color: "default" };
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    },
    {
      title: "Archivo / Carpeta",
      dataIndex: "item_name",
      key: "item_name",
      render: (v, row) => <Text>{v || row.detalle || "—"}</Text>
    },
    {
      title: "Ruta",
      dataIndex: "item_path",
      key: "item_path",
      render: (v) => <Text style={{ color: "#5b6b7c" }}>{v || "—"}</Text>
    },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 170,
      render: (v) => <Text style={{ color: "#5b6b7c" }}>{formatFecha(v)}</Text>
    },
    {
      title: "IP",
      dataIndex: "ip_user",
      key: "ip_user",
      width: 130,
      render: (v) => <Text style={{ color: "#5b6b7c" }}>{v || "—"}</Text>
    }
  ];

  return (
    <Drawer
      title="Historial del proyecto"
      open={open}
      onClose={onClose}
      width={720}
      destroyOnHidden
    >
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          value={filtroUsuario}
          onChange={setFiltroUsuario}
          style={{ width: 180 }}
          options={[
            { value: "todos", label: "Todos los usuarios" },
            ...usuarios.map((u) => ({ value: u, label: u }))
          ]}
        />
        <Select
          value={filtroAccion}
          onChange={setFiltroAccion}
          style={{ width: 180 }}
          options={[
            { value: "todos", label: "Todas las acciones" },
            ...Object.entries(ACCION_LABELS).map(([value, info]) => ({ value, label: info.label }))
          ]}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={registrosFiltrados}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
        scroll={{ x: 800 }}
      />
    </Drawer>
  );
}
